import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ThemeToggle } from '@/components/ThemeToggle';
import { FileText, Plus, Trash2, Loader2, ArrowLeft, Download, Eye, Lock, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SectionHeader({ title, onAdd, addLabel }) {
  return (
    <div className="flex justify-between items-center mb-3">
      <h3 className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{title}</h3>
      {onAdd && (
        <Button onClick={onAdd} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-3 py-1.5 text-sm">
          <Plus className="w-4 h-4 mr-1" /> {addLabel || 'Add'}
        </Button>
      )}
    </div>
  );
}

function FieldInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label className="text-sm text-slate-700 dark:text-slate-300">{label}</Label>
      <Input data-testid={testId} value={value} onChange={onChange} placeholder={placeholder} className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-transparent focus:border-[#FB923C] focus:ring-0 px-0 py-2 text-sm" />
    </div>
  );
}

export default function OnePageBuilder() {
  const navigate = useNavigate();
  const { isPro } = useSubscription();
  const [generating, setGenerating] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({ name: '', email: '', phone: '', linkedin: '', location: '' });
  const [objective, setObjective] = useState('');
  const [education, setEducation] = useState([{ degree: '', institution: '', year: '', gpa: '' }]);
  const [skills, setSkills] = useState({ technical: [], soft: [], languages: [] });
  const [projects, setProjects] = useState([]);
  const [experience, setExperience] = useState([]);
  const [certifications, setCertifications] = useState([]);

  const updatePI = useCallback((key, val) => setPersonalInfo(prev => ({ ...prev, [key]: val })), []);

  const buildData = useCallback(() => ({
    personal_info: personalInfo,
    professional_summary: objective,
    work_experience: experience,
    education,
    skills,
    projects,
    certifications,
  }), [personalInfo, objective, experience, education, skills, projects, certifications]);

  const handlePreview = useCallback(async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/resumes/one-page/preview`, { data: buildData() }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setGenerating(false);
    }
  }, [buildData]);

  const handleDownload = useCallback(async () => {
    if (!isPro) { toast.error('Upgrade to Pro to download'); navigate('/subscription'); return; }
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/resumes/one-page/download`, { data: buildData() }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'one-page-resume.pdf'; a.click();
      toast.success('Resume downloaded!');
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Pro subscription required' : 'Download failed');
    } finally {
      setGenerating(false);
    }
  }, [buildData, isPro, navigate]);

  const handleSaveToBuilder = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/resumes`, {
        title: personalInfo.name ? `${personalInfo.name}'s Resume` : 'One-Page Resume',
        template: 'minimal',
        data: buildData()
      }, { withCredentials: true });
      navigate(`/builder/${data.id}`);
      toast.success('Saved! Continue editing in the full builder.');
    } catch (error) {
      toast.error('Failed to save');
    }
  }, [buildData, personalInfo.name, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
            <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button onClick={() => navigate('/dashboard')} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-none px-4 py-2 text-sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FB923C] mb-3 block">FRESHER RESUME</span>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="one-page-heading">
            One-Page Resume Builder
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400">LaTeX-quality PDF, ATS-friendly, designed for freshers</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form - 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            {/* Personal Info */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Personal Info" />
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Full Name" value={personalInfo.name} onChange={(e) => updatePI('name', e.target.value)} testId="op-name" />
                <FieldInput label="Email" value={personalInfo.email} onChange={(e) => updatePI('email', e.target.value)} testId="op-email" />
                <FieldInput label="Phone" value={personalInfo.phone} onChange={(e) => updatePI('phone', e.target.value)} testId="op-phone" />
                <FieldInput label="Location" value={personalInfo.location} onChange={(e) => updatePI('location', e.target.value)} testId="op-location" />
                <div className="col-span-2">
                  <FieldInput label="LinkedIn" value={personalInfo.linkedin} onChange={(e) => updatePI('linkedin', e.target.value)} testId="op-linkedin" />
                </div>
              </div>
            </div>

            {/* Objective */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Career Objective" />
              <Textarea data-testid="op-objective" value={objective} onChange={(e) => setObjective(e.target.value)} rows={3} placeholder="2-3 sentences about your career goals..." className="rounded-none border-slate-200 dark:border-slate-600 dark:bg-slate-900 text-sm" />
            </div>

            {/* Education */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Education" onAdd={() => setEducation(p => [...p, { degree: '', institution: '', year: '', gpa: '' }])} addLabel="Add" />
              {education.map((edu, idx) => (
                <div key={`edu-${idx}`} className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
                  <FieldInput label="Degree" value={edu.degree} onChange={(e) => { const u=[...education]; u[idx].degree=e.target.value; setEducation(u); }} testId={`op-edu-degree-${idx}`} />
                  <FieldInput label="Institution" value={edu.institution} onChange={(e) => { const u=[...education]; u[idx].institution=e.target.value; setEducation(u); }} testId={`op-edu-inst-${idx}`} />
                  <FieldInput label="Year" value={edu.year} onChange={(e) => { const u=[...education]; u[idx].year=e.target.value; setEducation(u); }} />
                  <div className="flex gap-2 items-end">
                    <div className="flex-1"><FieldInput label="GPA" value={edu.gpa} onChange={(e) => { const u=[...education]; u[idx].gpa=e.target.value; setEducation(u); }} /></div>
                    {education.length > 1 && <Button onClick={() => setEducation(p => p.filter((_,i)=>i!==idx))} className="bg-white dark:bg-slate-700 border border-red-200 dark:border-red-800 text-red-600 rounded-none px-2 py-1.5 mb-0.5"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </div>
              ))}
            </div>

            {/* Skills */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Skills" />
              {['technical', 'soft', 'languages'].map((cat) => (
                <div key={cat} className="mb-3">
                  <Label className="text-sm text-slate-700 dark:text-slate-300 capitalize">{cat}</Label>
                  <Input data-testid={`op-skills-${cat}`} value={skills[cat].join(', ')} onChange={(e) => setSkills(prev => ({...prev, [cat]: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} placeholder="Comma separated" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-transparent focus:border-[#FB923C] focus:ring-0 px-0 py-2 text-sm" />
                </div>
              ))}
            </div>

            {/* Projects */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Projects" onAdd={() => setProjects(p => [...p, { title: '', description: '', techStack: '' }])} addLabel="Add" />
              {projects.map((proj, idx) => (
                <div key={`proj-${idx}`} className="mb-3 pb-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      <FieldInput label="Title" value={proj.title} onChange={(e) => { const u=[...projects]; u[idx].title=e.target.value; setProjects(u); }} testId={`op-proj-title-${idx}`} />
                      <FieldInput label="Tech Stack" value={proj.techStack} onChange={(e) => { const u=[...projects]; u[idx].techStack=e.target.value; setProjects(u); }} />
                      <div><Label className="text-sm text-slate-700 dark:text-slate-300">Description</Label>
                      <Textarea value={proj.description} onChange={(e) => { const u=[...projects]; u[idx].description=e.target.value; setProjects(u); }} rows={2} className="rounded-none border-slate-200 dark:border-slate-600 dark:bg-slate-900 text-sm" /></div>
                    </div>
                    <Button onClick={() => setProjects(p => p.filter((_,i)=>i!==idx))} className="bg-white dark:bg-slate-700 border border-red-200 dark:border-red-800 text-red-600 rounded-none px-2 py-1.5 mt-6"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader title="Certifications" onAdd={() => setCertifications(p => [...p, { name: '', issuer: '', year: '' }])} addLabel="Add" />
              {certifications.map((cert, idx) => (
                <div key={`cert-${idx}`} className="grid grid-cols-3 gap-3 mb-2">
                  <FieldInput label="Name" value={cert.name} onChange={(e) => { const u=[...certifications]; u[idx].name=e.target.value; setCertifications(u); }} />
                  <FieldInput label="Issuer" value={cert.issuer} onChange={(e) => { const u=[...certifications]; u[idx].issuer=e.target.value; setCertifications(u); }} />
                  <div className="flex gap-2 items-end">
                    <div className="flex-1"><FieldInput label="Year" value={cert.year} onChange={(e) => { const u=[...certifications]; u[idx].year=e.target.value; setCertifications(u); }} /></div>
                    <Button onClick={() => setCertifications(p => p.filter((_,i)=>i!==idx))} className="bg-white dark:bg-slate-700 border border-red-200 dark:border-red-800 text-red-600 rounded-none px-2 py-1.5 mb-0.5"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar - 2 columns */}
          <div className="lg:col-span-2 space-y-4">
            <div className="sticky top-24">
              <div className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 shadow-[6px_6px_0px_#0F172A] dark:shadow-[6px_6px_0px_#38bdf8] p-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Actions</h3>
                <div className="space-y-3">
                  <Button data-testid="preview-btn" onClick={handlePreview} disabled={generating} className="w-full bg-white dark:bg-slate-700 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-white font-bold rounded-none px-4 py-3 transition-all hover:bg-slate-50 shadow-[3px_3px_0px_#0F172A] dark:shadow-none">
                    {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview PDF
                  </Button>
                  <Button data-testid="download-btn" onClick={handleDownload} disabled={generating} className="w-full bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-3 transition-all hover:-translate-y-1 shadow-[3px_3px_0px_#0F172A]">
                    {isPro ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Download PDF {!isPro && '(Pro)'}
                  </Button>
                  <Button data-testid="save-to-builder-btn" onClick={handleSaveToBuilder} className="w-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-none px-4 py-3 text-sm">
                    Save to Full Builder
                  </Button>
                </div>
                <div className="mt-5 p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-bold text-slate-900 dark:text-white mb-2">Tips for Freshers</p>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5">
                    <li>- Keep it to one page max</li>
                    <li>- Lead with education and skills</li>
                    <li>- Highlight projects over experience</li>
                    <li>- Use action verbs in descriptions</li>
                    <li>- Include relevant coursework if applicable</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
